import React, { useState, useEffect } from 'react';
import axios from 'axios';

const GenomicAnalysis = ({ crop }) => {
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  
  const [mafThreshold, setMafThreshold] = useState(0.05);
  const [missingDataThreshold, setMissingDataThreshold] = useState(0.20);
  const [removeSamplesThreshold, setRemoveSamplesThreshold] = useState(0.50);
  const [removeMonomorphic, setRemoveMonomorphic] = useState(true);
  const [imputationMethod, setImputationMethod] = useState('mode');
  const [radFilter, setRadFilter] = useState(null);
  
  const [qcResults, setQcResults] = useState(null);
  const [saveTableName, setSaveTableName] = useState('');
  const [saveResults, setSaveResults] = useState(null);

  useEffect(() => { fetchTables(); }, [crop]);

  const fetchTables = async () => {
    try {
      let url = '/api/genomic-analysis/tables';
      if (crop) url += `?crop=${encodeURIComponent(crop)}`;
      const response = await axios.get(url);
      setTables(response.data.tables);
    } catch (error) {
      console.error('Error fetching tables:', error);
    }
  };

  const runCombinedQC = async () => {
    if (!selectedTable) { alert('Please select a table'); return; }
    setLoading(true);
    try {
      const response = await axios.post('/api/genomic-analysis/combined-qc-analysis', {
        table_name: selectedTable,
        maf_threshold: mafThreshold,
        missing_data_threshold: missingDataThreshold,
        remove_samples_threshold: removeSamplesThreshold,
        remove_monomorphic: removeMonomorphic,
        imputation_method: imputationMethod,
        rad_filter: radFilter
      }, { timeout: 600000 });
      setQcResults(response.data);
      const suffix = radFilter ? `_${radFilter}row_filtered` : '_filtered';
      setSaveTableName(selectedTable + suffix);
      setStep(2);
    } catch (error) {
      console.error('Error in QC analysis:', error);
      alert('Error in QC analysis: ' + (error.response?.data?.detail || error.message));
    } finally { setLoading(false); }
  };

  const saveFilteredTable = async () => {
    if (!saveTableName.trim()) { alert('Please enter a name for the filtered table'); return; }
    setLoading(true);
    try {
      const response = await axios.post('/api/genomic-analysis/save-filtered-table', {
        table_name: selectedTable,
        new_table_name: saveTableName.trim(),
        passed_markers: qcResults.passed_markers,
        passed_samples: qcResults.passed_samples,
        filter_params: {
          maf_threshold: mafThreshold, missing_data_threshold: missingDataThreshold,
          remove_samples_threshold: removeSamplesThreshold, remove_monomorphic: removeMonomorphic,
          imputation_method: imputationMethod, rad_filter: radFilter,
        },
        summary: {
          original_markers: qcResults.original_markers, original_samples: qcResults.original_samples,
          filtered_markers: qcResults.filtered_markers, filtered_samples: qcResults.filtered_samples,
          markers_removed_maf: qcResults.markers_removed_maf,
          markers_removed_missing: qcResults.markers_removed_missing,
          markers_removed_monomorphic: qcResults.markers_removed_monomorphic,
          samples_removed: qcResults.samples_removed,
        },
      }, { timeout: 600000 });
      setSaveResults(response.data);
      setStep(3);
      alert('Filtered data saved as "' + response.data.new_table_name + '" with ' +
            response.data.rows_inserted + ' samples and ' + response.data.markers_kept + ' markers.');
    } catch (error) {
      console.error('Error saving filtered table:', error);
      alert('Error saving filtered table: ' + (error.response?.data?.detail || error.message));
    } finally { setLoading(false); }
  };

  const resetAnalysis = () => {
    setStep(1); setQcResults(null); setSaveResults(null); setSaveTableName('');
  };

  const stepIndicator = [
    { num: 1, label: 'Setup' },
    { num: 2, label: 'QC Results & Save' },
    { num: 3, label: 'Done' }
  ];

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <h2 style={{ color: '#025c4f', marginBottom: '10px' }}>&#x1F9EC; Genomic Data Analysis</h2>
      <p style={{ color: '#666', marginBottom: '30px' }}>
        Quality control pipeline: MAF filtering, missing data removal, and save filtered data to database
      </p>

      {/* Step Indicator */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px',
        borderBottom: '2px solid #e0e0e0', paddingBottom: '10px' }}>
        {stepIndicator.map((s) => (
          <div key={s.num} style={{
            flex: 1, textAlign: 'center', padding: '10px', borderRadius: '8px',
            backgroundColor: step >= s.num ? '#dbf8f4' : '#f5f5f5',
            color: step >= s.num ? '#025c4f' : '#999',
            fontWeight: step === s.num ? 'bold' : 'normal'
          }}>
            <div style={{ fontSize: '24px' }}>{s.num}</div>
            <div style={{ fontSize: '12px' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Loading overlay */}
      {loading && (
        <div style={{ padding: '30px', textAlign: 'center', backgroundColor: '#fff3cd',
          borderRadius: '8px', marginBottom: '20px', border: '1px solid #ffc107' }}>
          <div style={{ fontSize: '24px', marginBottom: '10px' }}>&#x23F3;</div>
          <div style={{ fontWeight: 'bold', color: '#856404' }}>
            Processing... This may take a few minutes for large datasets.
          </div>
          <div style={{ marginTop: '15px', height: '4px', backgroundColor: '#ffeaa7',
            borderRadius: '2px', overflow: 'hidden' }}>
            <div style={{ height: '100%', backgroundColor: '#f39c12', borderRadius: '2px',
              animation: 'pulse 1.5s ease-in-out infinite', width: '60%' }} />
          </div>
          <style>{'@keyframes pulse { 0%,100% { opacity: 0.4; width: 30%; } 50% { opacity: 1; width: 80%; } }'}</style>
        </div>
      )}

      {/* Step 1: Setup */}
      {step === 1 && (
        <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
          <h3 style={{ color: '#025c4f', marginBottom: '10px' }}>&#x1F4CB; Step 1: Configure Analysis Parameters</h3>
          <p style={{ color: '#666', fontSize: '14px', marginBottom: '20px' }}>
            Defaults match standard wheat/barley genomics publications
          </p>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>Select Master Table:</label>
            <select value={selectedTable} onChange={(e) => setSelectedTable(e.target.value)}
              style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px' }}>
              <option value="">-- Select a table --</option>
              {tables.map(table => (<option key={table} value={table}>{table}</option>))}
            </select>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
              &#x1F33E; Row Type Filter (Barley):
            </label>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button onClick={() => setRadFilter(radFilter === '2' ? null : '2')}
                style={{ padding: '8px 20px', borderRadius: '20px', fontSize: '14px', fontWeight: '600',
                  cursor: 'pointer', border: radFilter === '2' ? '2px solid #2e86de' : '1px solid #adb5bd',
                  backgroundColor: radFilter === '2' ? '#2e86de' : '#fff',
                  color: radFilter === '2' ? '#fff' : '#495057' }}>2-Row</button>
              <button onClick={() => setRadFilter(radFilter === '6' ? null : '6')}
                style={{ padding: '8px 20px', borderRadius: '20px', fontSize: '14px', fontWeight: '600',
                  cursor: 'pointer', border: radFilter === '6' ? '2px solid #e67e22' : '1px solid #adb5bd',
                  backgroundColor: radFilter === '6' ? '#e67e22' : '#fff',
                  color: radFilter === '6' ? '#fff' : '#495057' }}>6-Row</button>
              <span style={{ fontSize: '13px', color: '#888', marginLeft: '8px' }}>
                {radFilter ? `Analyzing only ${radFilter}-row barley` : 'Analyzing all samples'}
              </span>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginTop: '25px' }}>
            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
                MAF Threshold (keep markers &ge; this value):
              </label>
              <input type="number" min="0" max="0.5" step="0.01" value={mafThreshold}
                onChange={(e) => setMafThreshold(parseFloat(e.target.value))}
                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px' }} />
              <small style={{ color: '#666' }}>Standard: &ge; 0.05 (5%)</small>
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
                SNP Missing Threshold (remove markers &gt; this %):
              </label>
              <input type="number" min="0" max="1" step="0.05" value={missingDataThreshold}
                onChange={(e) => setMissingDataThreshold(parseFloat(e.target.value))}
                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px' }} />
              <small style={{ color: '#666' }}>Standard: &le; 20%</small>
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>
                Sample Missing Threshold (remove samples &gt; this %):
              </label>
              <input type="number" min="0" max="1" step="0.05" value={removeSamplesThreshold}
                onChange={(e) => setRemoveSamplesThreshold(parseFloat(e.target.value))}
                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px' }} />
              <small style={{ color: '#666' }}>Standard: &le; 50%</small>
            </div>
            <div>
              <label style={{ display: 'flex', alignItems: 'center', fontWeight: 'bold', marginBottom: '8px' }}>
                <input type="checkbox" checked={removeMonomorphic}
                  onChange={(e) => setRemoveMonomorphic(e.target.checked)}
                  style={{ marginRight: '8px', width: '18px', height: '18px', cursor: 'pointer' }} />
                Remove Monomorphic Markers
              </label>
              <small style={{ color: '#666', display: 'block', marginLeft: '26px' }}>
                Standard: Yes (markers with only one allele)
              </small>
            </div>
            <div>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>Imputation Method:</label>
              <select value={imputationMethod} onChange={(e) => setImputationMethod(e.target.value)}
                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px' }}>
                <option value="mode">Mode (Most Common Allele)</option>
                <option value="random">Random (Frequency-Based)</option>
                <option value="mean_marker">Mean Marker (Beagle-style)</option>
                <option value="none">None (Keep Failed Values)</option>
              </select>
            </div>
          </div>

          <button onClick={runCombinedQC} disabled={!selectedTable || loading}
            style={{ marginTop: '25px', padding: '12px 30px',
              backgroundColor: selectedTable ? '#025c4f' : '#ccc', color: 'white',
              border: 'none', borderRadius: '6px',
              cursor: selectedTable ? 'pointer' : 'not-allowed',
              fontSize: '16px', fontWeight: 'bold' }}>
            {loading ? 'Analyzing...' : '\uD83D\uDE80 Run QC Analysis \u2192'}
          </button>
        </div>
      )}

      {/* Step 2: Combined QC Results + Save */}
      {step === 2 && qcResults && (
        <div>
          {/* MAF Analysis */}
          <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
            <h3 style={{ color: '#025c4f', marginBottom: '20px' }}>&#x1F4CA; MAF Analysis</h3>
            
            <div style={{ padding: '12px 18px', marginBottom: '18px', backgroundColor: '#e8f4f8',
              borderRadius: '8px', border: '1px solid #17a2b8', fontSize: '14px', color: '#0c5460' }}>
              &#x1F9EC; Analyzed <strong>{(qcResults.total_samples || 0).toLocaleString()}</strong>{' '}
              {qcResults.rad_filter ? `${qcResults.rad_filter}-row ` : ''}samples across{' '}
              <strong>{qcResults.total_markers.toLocaleString()}</strong> markers
              {qcResults.markers_excluded_missing > 0 && (
                <span> ({qcResults.markers_excluded_missing.toLocaleString()} excluded due to &gt;
                {((qcResults.missing_data_threshold || missingDataThreshold) * 100).toFixed(0)}% missing)</span>
              )}
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '15px', marginBottom: '25px' }}>
              <div style={{ padding: '15px', backgroundColor: '#e8f5e9', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#2e7d32' }}>
                  {(qcResults.total_samples || 0).toLocaleString()}
                </div>
                <div style={{ color: '#666', fontSize: '14px' }}>
                  {qcResults.rad_filter ? `${qcResults.rad_filter}-Row ` : ''}Samples
                </div>
              </div>
              <div style={{ padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#025c4f' }}>
                  {qcResults.total_markers.toLocaleString()}
                </div>
                <div style={{ color: '#666', fontSize: '14px' }}>Markers Analyzed</div>
              </div>
              <div style={{ padding: '15px', backgroundColor: '#fff3cd', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#856404' }}>
                  {qcResults.markers_below_threshold}
                </div>
                <div style={{ color: '#666', fontSize: '14px' }}>Below MAF {mafThreshold}</div>
              </div>
              <div style={{ padding: '15px', backgroundColor: '#d1ecf1', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#0c5460' }}>
                  {qcResults.markers_passing_maf}
                </div>
                <div style={{ color: '#666', fontSize: '14px' }}>Markers Passing MAF</div>
              </div>
            </div>

            <h4 style={{ marginBottom: '15px' }}>MAF Distribution:</h4>
            <div style={{ marginBottom: '20px' }}>
              {Object.entries(qcResults.maf_distribution).map(([range, count]) => (
                <div key={range} style={{ marginBottom: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
                    <span style={{ fontWeight: 'bold' }}>MAF {range}:</span>
                    <span>{count} markers</span>
                  </div>
                  <div style={{ width: '100%', height: '20px', backgroundColor: '#e0e0e0',
                    borderRadius: '4px', overflow: 'hidden' }}>
                    <div style={{ width: `${(count / Math.max(qcResults.total_markers, 1)) * 100}%`,
                      height: '100%', backgroundColor: '#025c4f' }} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Missing Data */}
          <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
            <h3 style={{ color: '#025c4f', marginBottom: '20px' }}>&#x26A0;&#xFE0F; Missing Data Analysis</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px', marginBottom: '25px' }}>
              <div style={{ padding: '15px', backgroundColor: '#f8d7da', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#721c24' }}>
                  {qcResults.markers_above_threshold}
                </div>
                <div style={{ color: '#666', fontSize: '14px' }}>
                  Markers &gt;{(missingDataThreshold * 100).toFixed(0)}% Missing
                </div>
              </div>
              <div style={{ padding: '15px', backgroundColor: '#f8d7da', borderRadius: '8px', textAlign: 'center' }}>
                <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#721c24' }}>
                  {qcResults.samples_above_threshold}
                </div>
                <div style={{ color: '#666', fontSize: '14px' }}>
                  Samples &gt;{(removeSamplesThreshold * 100).toFixed(0)}% Missing
                </div>
              </div>
            </div>

            {qcResults.sample_missing && qcResults.sample_missing.length > 0 && (
              <div>
                <h4 style={{ marginBottom: '15px' }}>Top 10 Worst Samples (by missing rate):</h4>
                <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #ddd', borderRadius: '4px' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0 }}>
                      <tr>
                        <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #ddd' }}>Cultivar</th>
                        <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Missing Rate</th>
                        <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #ddd' }}>Failed / Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {qcResults.sample_missing.slice(0, 10).map((sample, idx) => (
                        <tr key={idx} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '10px' }}>{sample.cultivar}</td>
                          <td style={{ padding: '10px', textAlign: 'center',
                            color: sample.missing_rate > removeSamplesThreshold ? '#721c24' : '#000',
                            fontWeight: sample.missing_rate > removeSamplesThreshold ? 'bold' : 'normal' }}>
                            {(sample.missing_rate * 100).toFixed(2)}%
                          </td>
                          <td style={{ padding: '10px', textAlign: 'center' }}>
                            {sample.failed_count} / {sample.total_markers}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          {/* Filter Summary + Save */}
          <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '8px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
            <h3 style={{ color: '#025c4f', marginBottom: '20px' }}>&#x2705; Filtering Summary</h3>
            
            <div style={{ backgroundColor: '#d4edda', padding: '20px', borderRadius: '8px',
              marginBottom: '25px', border: '1px solid #c3e6cb' }}>
              <h4 style={{ color: '#155724', marginBottom: '15px' }}>Quality Control Summary:</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                <div>
                  <strong>Original Data:</strong>
                  <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                    <li>{(qcResults.original_markers || 0).toLocaleString()} markers</li>
                    <li>{(qcResults.original_samples || 0).toLocaleString()} samples</li>
                  </ul>
                </div>
                <div>
                  <strong>Filtered Data:</strong>
                  <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                    <li style={{ color: '#025c4f', fontWeight: 'bold' }}>
                      {(qcResults.filtered_markers || 0).toLocaleString()} markers
                    </li>
                    <li style={{ color: '#025c4f', fontWeight: 'bold' }}>
                      {(qcResults.filtered_samples || 0).toLocaleString()} samples
                    </li>
                  </ul>
                </div>
              </div>
              <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #c3e6cb' }}>
                <strong>Removed:</strong>
                <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                  <li>{qcResults.markers_removed_maf} markers (MAF &lt; {mafThreshold})</li>
                  <li>{qcResults.markers_removed_missing} markers (missing &gt; {(missingDataThreshold * 100).toFixed(0)}%)</li>
                  {qcResults.markers_removed_monomorphic > 0 && (
                    <li>{qcResults.markers_removed_monomorphic} monomorphic markers</li>
                  )}
                  <li>{qcResults.samples_removed} samples (missing &gt; {(removeSamplesThreshold * 100).toFixed(0)}%)</li>
                </ul>
              </div>
              <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #c3e6cb' }}>
                <strong>Data Retention:</strong>
                <ul style={{ marginTop: '8px', paddingLeft: '20px' }}>
                  <li>Markers: {qcResults.original_markers > 0 ? ((qcResults.filtered_markers / qcResults.original_markers) * 100).toFixed(1) : 0}%</li>
                  <li>Samples: {qcResults.original_samples > 0 ? ((qcResults.filtered_samples / qcResults.original_samples) * 100).toFixed(1) : 0}%</li>
                </ul>
              </div>
            </div>

            {/* Save to Database */}
            <div style={{ backgroundColor: '#e8f4f8', padding: '20px', borderRadius: '8px',
              marginBottom: '20px', border: '1px solid #17a2b8' }}>
              <h4 style={{ color: '#0c5460', marginBottom: '15px' }}>&#x1F4BE; Save Filtered Data to Database</h4>
              <p style={{ color: '#0c5460', fontSize: '14px', marginBottom: '15px' }}>
                Creates a new table with only the filtered genotypes
                ({(qcResults.filtered_samples || 0).toLocaleString()} samples &times; {(qcResults.filtered_markers || 0).toLocaleString()} markers).
                The new table can be used directly for downstream analysis (PCA, genomic selection, etc).
              </p>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '10px' }}>
                <label style={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>Table Name:</label>
                <input type="text" value={saveTableName} onChange={(e) => setSaveTableName(e.target.value)}
                  style={{ flex: 1, padding: '10px', borderRadius: '4px', border: '1px solid #ddd', fontSize: '14px' }}
                  placeholder="Enter name for filtered table..." />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button onClick={saveFilteredTable} disabled={loading || !saveTableName.trim()}
                style={{ padding: '12px 30px',
                  backgroundColor: (loading || !saveTableName.trim()) ? '#ccc' : '#025c4f',
                  color: 'white', border: 'none', borderRadius: '6px',
                  cursor: (loading || !saveTableName.trim()) ? 'not-allowed' : 'pointer',
                  fontSize: '16px', fontWeight: 'bold' }}>
                {loading ? 'Saving...' : '\uD83D\uDCBE Save Filtered Data to Database'}
              </button>
              <button onClick={resetAnalysis}
                style={{ padding: '12px 30px', backgroundColor: '#6c757d', color: 'white',
                  border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '16px' }}>
                &larr; Back to Setup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Done */}
      {step === 3 && saveResults && (
        <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
          <h3 style={{ color: '#025c4f', marginBottom: '20px' }}>&#x1F389; Analysis Complete</h3>
          <div style={{ backgroundColor: '#d4edda', padding: '20px', borderRadius: '8px',
            marginBottom: '25px', border: '1px solid #c3e6cb' }}>
            <h4 style={{ color: '#155724', marginBottom: '15px' }}>&#x2705; Filtered Data Saved Successfully!</h4>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              <div>
                <p style={{ margin: '5px 0' }}><strong>New Table:</strong> {saveResults.new_table_name}</p>
                <p style={{ margin: '5px 0' }}><strong>Samples Saved:</strong> {(saveResults.rows_inserted || 0).toLocaleString()}</p>
                <p style={{ margin: '5px 0' }}><strong>Markers Kept:</strong> {(saveResults.markers_kept || 0).toLocaleString()}</p>
              </div>
              <div>
                <p style={{ margin: '5px 0' }}><strong>Filter ID:</strong> {saveResults.filter_id}</p>
                <p style={{ margin: '5px 0', color: '#155724', fontSize: '13px', marginTop: '10px' }}>
                  &#x1F4A1; The new table is now available in the table dropdown for all analyses
                  (PCA, genomic selection, similarity, etc).
                </p>
              </div>
            </div>
          </div>
          <button onClick={resetAnalysis}
            style={{ padding: '12px 30px', backgroundColor: '#025c4f', color: 'white',
              border: 'none', borderRadius: '6px', cursor: 'pointer',
              fontSize: '16px', fontWeight: 'bold' }}>
            &#x1F504; New Analysis
          </button>
        </div>
      )}
    </div>
  );
};

export default GenomicAnalysis;
